import { Link } from 'react-router-dom';
import Reveal from '@/components/feature/Reveal';
import PerformanceBoundary from '@/components/feature/PerformanceBoundary';
import ProofShot from './ProofShot';
import { openSupportChat } from '@/utils/supportChat';

export default function AccountTrust() {
  return (
    <section id="account" className="py-14 md:py-20 bg-background-100 border-t border-background-200 scroll-mt-32 md:scroll-mt-36">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <PerformanceBoundary label="account details">
            {(data) => {
              const accountSnapshot = data.accountSnapshot;
              const uscHint =
                'USC = US Cents — the smallest cash unit of the US dollar (1 USC = US$0.01). This is a cent account, so MetaTrader reports every deposit, withdrawal and profit in cents rather than whole dollars.';
              const accountFacts: { label: string; value: string; hint?: string }[] = [
                { label: 'Account Number', value: `#${accountSnapshot.accountNumber}` },
                { label: 'Broker', value: accountSnapshot.broker },
                { label: 'Account Type', value: accountSnapshot.accountType },
                { label: 'Platform', value: accountSnapshot.platform },
                { label: 'Currency', value: `${accountSnapshot.currency} · US Cents`, hint: uscHint },
                { label: 'Starting Deposit', value: `${accountSnapshot.startingDeposit.toLocaleString('en-US')} USC` },
                { label: 'Withdrawal', value: `${accountSnapshot.withdrawal.toFixed(2)} USC` },
                {
                  label: 'Current Balance',
                  value: `${accountSnapshot.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })} USC`,
                },
              ];

              return (
                <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                  <Reveal>
                    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                      <div className="flex items-center gap-3">
                        <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                          <i className="ri-shield-check-line text-xl" aria-hidden="true"></i>
                        </span>
                        <div>
                          <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                            The account behind these numbers
                          </h2>
                          <p className="text-xs md:text-sm text-foreground-500">
                            {accountSnapshot.verification}
                          </p>
                        </div>
                      </div>

                      <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        {accountFacts.map((fact) => (
                          <div key={fact.label} className="flex flex-col gap-1 border-b border-background-200 pb-3">
                            <dt className="text-xs text-foreground-500">{fact.label}</dt>
                            <dd className="font-num text-sm md:text-base font-semibold text-foreground-950 break-words">
                              <span className="inline-flex items-start gap-1.5 flex-wrap">
                                {fact.value}
                                {fact.hint && (
                                  <span className="group relative inline-flex items-center">
                                    <button
                                      type="button"
                                      className="w-4 h-4 flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors cursor-help"
                                      aria-label={fact.hint}
                                    >
                                      <i className="ri-information-line text-sm" aria-hidden="true"></i>
                                    </button>
                                    <span
                                      role="tooltip"
                                      className="pointer-events-none absolute left-0 top-full z-30 mt-2 w-64 rounded-md border border-background-300 bg-background-50 p-3 text-left text-xs font-normal leading-relaxed text-foreground-600 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100"
                                    >
                                      {fact.hint}
                                    </span>
                                  </span>
                                )}
                              </span>
                            </dd>
                          </div>
                        ))}
                      </dl>

                      <div className="mt-5 flex items-start gap-2.5 rounded-md bg-background-100 px-3.5 py-3">
                        <i className="ri-calculator-line mt-0.5 text-base text-primary-600" aria-hidden="true"></i>
                        <p className="text-xs leading-relaxed text-foreground-600">
                          <span className="font-semibold text-foreground-900">Current Balance</span> = Starting
                          Deposit − Withdrawal + Cumulative Net P&L ={' '}
                          <span className="font-num">
                            {accountSnapshot.startingDeposit.toLocaleString('en-US')} −{' '}
                            {accountSnapshot.withdrawal.toFixed(2)} +{' '}
                            {accountSnapshot.netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>{' '}
                          ={' '}
                          <span className="font-num font-semibold text-foreground-950">
                            {accountSnapshot.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })} USC
                          </span>
                          . Deposits and the one withdrawal sit outside the trading result, so every figure below
                          traces straight back to the closed trades.
                        </p>
                      </div>
                    </div>
                  </Reveal>

                  <Reveal delay={120}>
                    <div>
                      <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 text-balance">
                        Want the same setup on your account?
                      </h2>
                      <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                        The EA is free. Ask in the support chat for the EA file and a preset sized to your own
                        balance — we will walk you through getting it running on your account.
                      </p>
                      <div className="mt-7 flex flex-col sm:flex-row gap-3">
                        <button
                          type="button"
                          onClick={openSupportChat}
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-accent-500 hover:bg-accent-600 px-6 py-3.5 text-sm font-semibold text-primary-950 transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
                          Ask for the EA
                        </button>
                        <Link
                          to="/how-it-works"
                          className="inline-flex items-center justify-center gap-2 rounded-full border border-background-300 hover:bg-background-50 px-6 py-3.5 text-sm font-semibold text-foreground-900 transition-colors whitespace-nowrap"
                        >
                          How the strategy works
                          <i className="ri-arrow-right-line" aria-hidden="true"></i>
                        </Link>
                      </div>
                    </div>
                  </Reveal>
                </div>

                {/* Receipts — the raw, unedited sources behind the numbers above */}
                <div className="mt-12 border-t border-background-200 pt-10">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                        Receipts, not just numbers
                      </h2>
                      <p className="mt-2 max-w-2xl text-sm text-foreground-600">
                        Anyone can render a chart. These are the hard sources behind the numbers above —
                        the live trade history straight from the terminal, and the official account
                        statement the platform exports — so you can check them against the tables yourself.
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap text-accent-900">
                      <i className="ri-verified-badge-line" aria-hidden="true"></i>
                      Unedited sources
                    </span>
                  </div>

                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    <ProofShot
                      title="MT5 · History"
                      status={`account #${accountSnapshot.accountNumber}`}
                      statusIcon="ri-hashtag"
                      image="/image/mt5-history.png"
                      alt={`MetaTrader 5 trade history list for account ${accountSnapshot.accountNumber}`}
                      caption="The most recent closed trades straight from the MetaTrader 5 terminal — these rows line up with the Recent Closed Trades table above. The live list runs much longer than one screen; the complete history is available on request."
                      slotLabel="MT5 trade history screenshot (top of the list)"
                      excerptNote="Most recent trades · full history on request"
                    />
                    <ProofShot
                      title="MT5 · Account statement"
                      status="official export"
                      statusIcon="ri-file-list-3-line"
                      image="/image/mt5-statement.png"
                      alt={`MetaTrader 5 exported account statement report for account ${accountSnapshot.accountNumber}`}
                      caption={`The formal statement exported straight from MetaTrader 5 by ${accountSnapshot.broker} on account ${accountSnapshot.accountNumber} — account header, trade history and the built-in equity chart in one document. An excerpt is shown; the full report is available on request.`}
                      slotLabel="MT5 statement report export (header section)"
                      excerptNote="Excerpt · full report on request"
                      delay={120}
                    />
                  </div>

                  <p className="mt-4 flex items-start gap-2 text-xs text-foreground-500">
                    <i className="ri-information-line mt-0.5 text-foreground-400" aria-hidden="true"></i>
                    <span>
                      Both sources run far longer than a single screen, so only the most recent portion is
                      shown here. Nothing is edited apart from hiding login credentials — the account number
                      and dates stay visible on purpose, and the full statement is available on request.
                    </span>
                  </p>
                </div>
                </>
              );
            }}
          </PerformanceBoundary>
        </div>
      </div>
    </section>
  );
}